import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { getModeFromPath } from "../utils/getMode";
import { modeTheme } from "../theme/modeTheme";

import DisplayHomeLogo from "../../assets/logos/Display-Home-Logo.png";
import DisplayBusinessLogo from "../../assets/logos/Display-Business-Logo.png";
import DisplayEduLogo from "../../assets/logos/Display-Edu-Logo.png";
import AdminLogo from "../../assets/logos/Admin-logo.png";
import CampusLogo from "../../assets/logos/Campus-Logo.png";
import PagesLogo from "../../assets/logos/Pages-Logo.png";
import PagesIcon from "../../assets/logos/Pages Icon.png";
import TablesIcon from "../../assets/logos/Tables icon.png";
import DevicesIcon from "../../assets/logos/Devices icon.png";
import SportsLogo from "../../assets/logos/Sports-Logo.png";

const FarmIcon = "/app-icons/farm-icon.svg";
const NightstandIcon = "/app-icons/nightstand-icon.svg";
const FalconIcon = "/app-icons/sports-falcon-icon.svg";
const loadedLoadingIcons = new Set();

const logoMap = {
  home: DisplayHomeLogo,
  business: DisplayBusinessLogo,
  edu: DisplayEduLogo,
  admin: AdminLogo,
  campus: CampusLogo,
  pages: PagesLogo,
  tables: TablesIcon,
  devices: DevicesIcon,
  sports: FalconIcon,
  farm: FarmIcon,
  nightstand: NightstandIcon,
  tv: NightstandIcon,
};

function isIconReady(src = "") {
  return !src || loadedLoadingIcons.has(src);
}

function loadLoadingIcon(src = "") {
  if (!src || loadedLoadingIcons.has(src) || typeof window === "undefined") {
    return Promise.resolve(src);
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.onload = async () => {
      try {
        if (typeof image.decode === "function") await image.decode();
      } catch (_error) {
        // Loaded is enough for this tiny transition; decode can fail on some browsers.
      }
      loadedLoadingIcons.add(src);
      resolve(src);
    };
    image.onerror = () => resolve("");
    image.src = src;
  });
}

const modeLabelMap = {
  home: "Home",
  business: "Business",
  edu: "Education",
  nightstand: "Nightstand",
  admin: "Admin",
  campus: "Campus",
  pages: "Pages",
  tables: "Tables",
  devices: "Managed Devices",
  classroom: "Classroom",
  sports: "Sports",
  farm: "Farm",
};

function hexToRgb(color = "") {
  const clean = String(color || "").trim().replace("#", "");
  const normalized = clean.length === 3
    ? clean.split("").map((item) => `${item}${item}`).join("")
    : clean;
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function colorWithAlpha(color = "", alpha = 1) {
  const rgb = hexToRgb(color);
  if (!rgb) return `rgba(47, 110, 163, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export default function GlobalLoadingPage({
  title = "Loading",
  detail = "Preparing your workspace...",
  modeOverride = "",
  primaryColorOverride = "",
  iconOverride = "",
  iconFallbackOverride = "",
  iconPlaceholderOnly = false,
  cleanIconLoader = false,
  labelOverride = "",
  backgroundImageOverride = "",
}) {
  const location = useLocation();
  const mode = modeOverride || getModeFromPath(location.pathname, window.location.hostname);
  const primaryColor = primaryColorOverride || modeTheme[mode]?.primary || "#2f6ea3";
  const logo = logoMap[mode] || DisplayHomeLogo;
  const modeLabel = labelOverride || modeLabelMap[mode] || "Oikos";
  const isIconLoaderMode = iconPlaceholderOnly || Boolean(iconOverride) || ["pages", "tables", "devices", "sports", "farm", "nightstand", "tv"].includes(mode);
  const defaultIconImage =
    mode === "devices" ? DevicesIcon :
    mode === "tables" ? TablesIcon :
    mode === "sports" ? FalconIcon :
    mode === "farm" ? FarmIcon :
    mode === "nightstand" || mode === "tv" ? NightstandIcon :
    PagesIcon;
  const requestedIconImage = iconPlaceholderOnly ? "" : iconOverride || defaultIconImage;
  const fallbackIconImage = iconPlaceholderOnly ? "" : iconFallbackOverride || defaultIconImage;
  const [loadedIconImage, setLoadedIconImage] = useState(() => (
    isIconReady(requestedIconImage) ? requestedIconImage : fallbackIconImage
  ));
  const [iconImageReady, setIconImageReady] = useState(() => (
    isIconReady(requestedIconImage) || (requestedIconImage && requestedIconImage === fallbackIconImage)
  ));
  const fallbackLetter = String(modeLabel || "O").trim().slice(0, 1).toUpperCase() || "O";

  useEffect(() => {
    let cancelled = false;
    const immediateIcon = isIconReady(requestedIconImage) ? requestedIconImage : fallbackIconImage;
    setIconImageReady(isIconReady(immediateIcon) || (immediateIcon && immediateIcon === requestedIconImage));
    setLoadedIconImage(immediateIcon);
    loadLoadingIcon(requestedIconImage).then((nextIcon) => {
      if (!cancelled && nextIcon) {
        setIconImageReady(isIconReady(nextIcon));
        setLoadedIconImage(nextIcon);
      }
    });
    if (fallbackIconImage && fallbackIconImage !== requestedIconImage) {
      loadLoadingIcon(fallbackIconImage);
    }
    return () => {
      cancelled = true;
    };
  }, [fallbackIconImage, requestedIconImage]);

  return (
    <div
      style={
        isIconLoaderMode
          ? {
              ...styles.page,
              background: `linear-gradient(180deg, ${colorWithAlpha(primaryColor, 0.08)} 0%, ${colorWithAlpha(primaryColor, 0.16)} 100%)`,
            }
          : styles.page
      }
    >
      {backgroundImageOverride ? (
        <img
          aria-hidden="true"
          alt=""
          src={backgroundImageOverride}
          style={styles.backgroundImage}
        />
      ) : null}
      <div
        style={
          isIconLoaderMode
            ? cleanIconLoader
              ? {
                  ...styles.cleanBackdrop,
                  background:
                    `radial-gradient(circle at 50% 38%, ${colorWithAlpha(primaryColor, 0.16)}, transparent 30%), rgba(255,255,255,0.48)`,
                }
              : {
                  ...styles.pagesBackdrop,
                  background:
                    `radial-gradient(circle at 50% 34%, ${colorWithAlpha(primaryColor, 0.18)}, transparent 34%), rgba(255,255,255,0.78)`,
                }
            : styles.backdrop
        }
      />

      <div style={styles.content}>
        {isIconLoaderMode ? (
          cleanIconLoader ? (
            <div style={styles.cleanIconStage}>
              <span style={{ ...styles.cleanIconHalo, background: colorWithAlpha(primaryColor, 0.16) }} />
              {!iconImageReady ? (
                <span style={{ ...styles.cleanIconFallbackMark, color: primaryColor }}>{fallbackLetter}</span>
              ) : null}
              {loadedIconImage ? (
                <img
                  src={loadedIconImage}
                  alt={`${modeLabel} icon`}
                  style={{ ...styles.cleanIconImage, opacity: iconImageReady ? 1 : 0 }}
                  onLoad={() => {
                    loadedLoadingIcons.add(loadedIconImage);
                    setIconImageReady(true);
                  }}
                />
              ) : null}
            </div>
          ) : (
            <div style={styles.pagesIconStage}>
              <span style={{ ...styles.pagesSheet, ...styles.pagesSheetBack }} />
              <span style={{ ...styles.pagesSheet, ...styles.pagesSheetMid }} />
              <span style={{ ...styles.pagesIconHalo, background: colorWithAlpha(primaryColor, 0.16) }} />
              <span
                style={{
                  ...styles.pagesIconGlass,
                  boxShadow: `0 24px 52px ${colorWithAlpha(primaryColor, 0.2)}, inset 0 1px 0 rgba(255,255,255,0.72)`,
                }}
              >
                {!iconImageReady ? (
                  <span style={{ ...styles.pagesIconFallbackMark, color: primaryColor }}>{fallbackLetter}</span>
                ) : null}
                {loadedIconImage ? (
                  <img
                    src={loadedIconImage}
                    alt={`${modeLabel} icon`}
                    style={{ ...styles.pagesIconImage, opacity: iconImageReady ? 1 : 0 }}
                    onLoad={() => {
                      loadedLoadingIcons.add(loadedIconImage);
                      setIconImageReady(true);
                    }}
                  />
                ) : null}
              </span>
            </div>
          )
        ) : (
          <div style={styles.logoWrap}>
            <img src={logo} alt={`${modeLabel} logo`} style={styles.logo} />
          </div>
        )}

        <div style={{ ...styles.badge, color: primaryColor, borderColor: colorWithAlpha(primaryColor, 0.25) }}>
          {modeLabel}
        </div>

        <h2 style={{ ...styles.title, color: primaryColor }}>{title}</h2>
        <p style={styles.detail}>{detail}</p>

        <div style={styles.loaderRow} aria-label={title} role="status">
          {[0, 1, 2].map((index) => (
            <span
              key={index}
              style={{
                ...styles.ball,
                background: primaryColor,
                animationDelay: `${index * 0.18}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    alignItems: "center",
    boxSizing: "border-box",
    display: "grid",
    inset: 0,
    justifyItems: "center",
    minHeight: "100dvh",
    padding: "24px",
    placeItems: "center",
    position: "fixed",
    transform: "none",
    width: "100vw",
    zIndex: 1200,
  },

  backdrop: {
    position: "absolute",
    inset: 0,
    background: "rgba(255, 255, 255, 0.78)",
    backdropFilter: "blur(8px)",
  },

  backgroundImage: {
    height: "100%",
    inset: 0,
    objectFit: "cover",
    pointerEvents: "none",
    position: "absolute",
    width: "100%",
  },

  pagesBackdrop: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at 50% 34%, rgba(56,92,255,0.18), transparent 34%), rgba(255,255,255,0.78)",
    backdropFilter: "blur(10px)",
  },

  cleanBackdrop: {
    position: "absolute",
    inset: 0,
    backdropFilter: "blur(4px)",
  },

  content: {
    position: "relative",
    width: "100%",
    maxWidth: "420px",
    padding: "20px 24px",
    textAlign: "center",
    transform: "none",
  },

  logoWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
  },

  logo: {
    width: "170px",
    maxWidth: "82%",
    objectFit: "contain",
  },

  pagesIconStage: {
    height: 156,
    margin: "0 auto 18px",
    position: "relative",
    width: 156,
  },

  cleanIconStage: {
    alignItems: "center",
    animation: "pages-loading-float 2.2s ease-in-out infinite",
    display: "flex",
    height: 168,
    justifyContent: "center",
    margin: "0 auto 18px",
    position: "relative",
    width: 168,
  },

  cleanIconHalo: {
    animation: "pages-loading-pulse 1.8s ease-in-out infinite",
    borderRadius: "50%",
    inset: 8,
    position: "absolute",
  },

  cleanIconImage: {
    filter: "drop-shadow(0 20px 36px rgba(15, 23, 42, 0.14))",
    height: 132,
    objectFit: "contain",
    position: "relative",
    transition: "opacity 120ms ease",
    width: 132,
    zIndex: 2,
  },

  cleanIconFallbackMark: {
    alignItems: "center",
    background: "rgba(255,255,255,0.74)",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: "50%",
    display: "inline-flex",
    fontSize: 48,
    fontWeight: 900,
    height: 104,
    justifyContent: "center",
    lineHeight: 1,
    position: "relative",
    width: 104,
    zIndex: 1,
  },

  pagesSheet: {
    background: "rgba(255,255,255,0.62)",
    border: "1px solid rgba(190,202,232,0.7)",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(23,32,51,0.12)",
    height: 92,
    left: 33,
    position: "absolute",
    top: 32,
    width: 90,
  },

  pagesSheetBack: {
    animation: "pages-loading-sheet-back 2.8s ease-in-out infinite",
    transform: "rotate(-10deg) translate(-16px, 12px)",
  },

  pagesSheetMid: {
    animation: "pages-loading-sheet-mid 2.8s ease-in-out infinite",
    transform: "rotate(8deg) translate(16px, 8px)",
  },

  pagesIconHalo: {
    animation: "pages-loading-pulse 1.8s ease-in-out infinite",
    background: "rgba(56,92,255,0.16)",
    borderRadius: "50%",
    inset: 16,
    position: "absolute",
  },

  pagesIconGlass: {
    alignItems: "center",
    animation: "pages-loading-float 2.2s ease-in-out infinite",
    backdropFilter: "blur(18px) saturate(1.2)",
    WebkitBackdropFilter: "blur(18px) saturate(1.2)",
    background: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(255,255,255,0.82)",
    borderRadius: 8,
    boxShadow: "0 24px 52px rgba(56,92,255,0.2), inset 0 1px 0 rgba(255,255,255,0.72)",
    display: "flex",
    height: 86,
    justifyContent: "center",
    left: 35,
    position: "absolute",
    top: 35,
    width: 86,
  },

  pagesIconImage: {
    height: 68,
    objectFit: "contain",
    position: "relative",
    transition: "opacity 120ms ease",
    width: 68,
    zIndex: 2,
  },

  pagesIconFallbackMark: {
    alignItems: "center",
    background: "rgba(255,255,255,0.74)",
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 8,
    display: "inline-flex",
    fontSize: 34,
    fontWeight: 900,
    height: 68,
    justifyContent: "center",
    left: 8,
    lineHeight: 1,
    position: "absolute",
    top: 8,
    width: 68,
    zIndex: 1,
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    border: "1px solid",
    background: "#fff",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: "14px",
  },

  title: {
    margin: "0 0 10px",
    fontSize: "24px",
    fontWeight: "700",
  },

  detail: {
    margin: "0 0 22px",
    color: "#475569",
    fontSize: "14px",
    lineHeight: "1.5",
  },

  loaderRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
  },

  ball: {
    width: "14px",
    height: "14px",
    borderRadius: "999px",
    animationName: "oikos-loading-bounce",
    animationDuration: "0.9s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
  },
};
